import { useAccountSwitcherUiAPIInterface } from "@dealer-center/ddc-pages-account-switcher-ui-fetch";
import type { AccountSwitcherUiAPIInterfaceInternal } from "@dealer-center/ddc-pages-account-switcher-ui-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  Alert,
  Box,
  Button,
  Table,
  Typography
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { formatDateRecordCreated } from "./helper";

export const PagesAccountSwitcherUiUiResults = () => {
  const { searchValue } = useParams();
  const { data, error, isLoading } = useAccountSwitcherUiAPIInterface({
    searchValue
  });
  const navigate = useNavigate();

  if (error)
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {error.message}
      </Alert>
    );

  if (isLoading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 4,
          height: "100%",
          alignItems: "center"
        }}
      >
        <LoadingIndicator
          actionName="Loading PagesAccountSwitcherUiUi"
          text="Loading"
        />
      </Box>
    );

  const columns = buildColumns(navigate, searchValue);
  return (
    <Box sx={{ pt: 4 }}>
      <Typography variant={"h2"}>Results</Typography>
      <Table.Table
        displayFilter
        sortableColumns
        columns={columns}
        data={data || []}
        emptyText="No data"
      />
      <Outlet />
    </Box>
  );
};

const buildColumns = (
  navigate: ReturnType<typeof useNavigate>,
  searchValue?: string
): TypeSafeTableColumns<AccountSwitcherUiAPIInterfaceInternal> => [
  {
    dataIndex: "name",
    title: "Name"
  },
  {
    dataIndex: "address",
    title: "Address"
  },
  {
    dataIndex: "phone",
    title: "Phone",
    columnSortable: false,
    render: (phone: string) => {
      return (
        <a href={`tel:${phone}`} rel="noreferrer">
          {phone}
        </a>
      );
    }
  },
  {
    dataIndex: "email",
    title: "Email",
    render: (email: string) => {
      return (
        <a href={`mailto:${email}`} rel="noreferrer">
          {email}
        </a>
      );
    }
  },
  {
    dataIndex: "website",
    title: "Website",
    render: (website: string) => {
      return (
        <a href={website} rel="noreferrer" target="_blank">
          {website}
        </a>
      );
    }
  },
  {
    dataIndex: "description",
    title: "Description"
  },
  {
    dataIndex: "dateRecordCreated",
    title: "Date Record Created",
    sorter: (
      recordA: AccountSwitcherUiAPIInterfaceInternal,
      recordB: AccountSwitcherUiAPIInterfaceInternal
    ) =>
      new Date(recordA.dateRecordCreated).getTime() -
      new Date(recordB.dateRecordCreated).getTime(),
    render: (dateRecordCreated: string) => {
      return formatDateRecordCreated(dateRecordCreated);
    }
  },
  {
    dataIndex: "images",
    title: "Images",
    sorter: (
      recordA: AccountSwitcherUiAPIInterfaceInternal,
      recordB: AccountSwitcherUiAPIInterfaceInternal
    ) => recordA.images.length - recordB.images.length,
    render: (
      images: string[],
      record: AccountSwitcherUiAPIInterfaceInternal
    ) => {
      return (
        <Button
          analyticsConfiguration={{
            eventName: "PagesAccountSwitcherUiUi Images"
          }}
          buttonStyle="secondary"
          onClick={() => {
            navigate(`/accountSwitcherUi/${searchValue}/${record.key}`);
          }}
        >
          {images.length} Images
        </Button>
      );
    }
  }
];
